<template>
	<item-filter :items="countries" :selected-item="selectedCountry" @filteredByItem="filterGamesByCountry"></item-filter>
	<div class="container mt-5">
		<div class="columns is-multiline">
			<add-card v-if="auth.state.isLoggedIn && currentPage === 1" @click="openAdd"></add-card>
			<template v-for="(g, j) in gamesPaginated" :key="j">
				<div class="column is-6 is-flex is-flex-direction-column is-justify-content-space-between">
					<div class="columns">
						<div class="column is-4">
							<div class="is-relative">
								<Carousel :items-to-show="itemsToShow" :mouse-drag="false">
									<Slide v-for="image in g.image.src" :key="image.id">
										<figure class="image" @click="openGameModal(g.image)">
											<img :src="image" :alt="g.image.alt" />
										</figure>
									</Slide>

									<template #addons>
										<Navigation />
									</template>
								</Carousel>
								<owned-or-not :owned="g.own"></owned-or-not>
							</div>
						</div>
						<div class="column is-8">
							<div class="is-flex is-justify-content-space-between is-align-items-flex-start">
								<h3 class="title is-size-5">{{ g.title }}</h3>
								<div class="admin-actions" v-if="g.id && auth.state.isLoggedIn">
									<button class="icon-button" title="Edit" @click="openEdit(g)">
										<i class="fas fa-pen"></i>
									</button>
									<button class="icon-button icon-button-delete" title="Delete" @click="onDelete(g)">
										<i class="fas fa-trash"></i>
									</button>
								</div>
							</div>
							<ul class="has-text-white p-3">
								<li><strong>Publisher:</strong> {{ g.publisher }}</li>
								<li><strong>Country:</strong> {{ g.country }}</li>
								<li><strong>EAN:</strong> {{ g.ean }}</li>
								<li><strong>Bigbox:</strong> {{ g.bigbox }}</li>
								<li><strong>Rarity:</strong> {{ g.rarity }}</li>
								<li><strong>Platform:</strong> {{ g.support }}</li>
							</ul>
						</div>
					</div>
					<hr />
				</div>
			</template>
		</div>
		<empty-hero v-if="gamesPaginated.length === 0"></empty-hero>
		<game-modal :game="gameModalContent" v-if="showGameModal" @close-modal="showGameModal = false"></game-modal>
		<Transition name="fade">
			<back-to-top v-if="currentPage > 1"></back-to-top>
		</Transition>
	</div>
	<add-game-modal :show="showAddModal" :editing-item="editingItem" :on-submit="handleSubmit" @close="onModalClose"></add-game-modal>
</template>

<script>
import COUNTRIES from '@/countries.js';
import GAMES from '@/games.js';
import GameModal from '@/components/GameModal.vue';
import ItemFilter from '@/components/ItemFilter.vue';
import BackToTop from '@/components/BackToTop.vue';
import EmptyHero from '@/components/EmptyHero.vue';
import OwnedOrNot from '@/components/OwnedOrNot.vue';
import AddCard from '@/components/AddCard.vue';
import AddGameModal from '@/components/AddGameModal.vue';
import auth from '@/auth.js';
import customItems from '@/customItems.js';
import { Carousel, Slide, Navigation } from 'vue3-carousel';
export default {
	name: 'OfficialView',
	props: ['modelValue', 'rarityFilter', 'ownFilter'],
	components: {
		Carousel,
		Slide,
		Navigation,
		GameModal,
		ItemFilter,
		EmptyHero,
		OwnedOrNot,
		BackToTop,
		AddCard,
		AddGameModal,
	},
	data() {
		return {
			auth,
			countries: COUNTRIES.filter((c) => !['India', 'Finland'].includes(c.name)),
			itemsToShow: 1,
			showGameModal: false,
			gameModalContent: null,
			currentPage: 1,
			perPage: 16,
			showAddModal: false,
			editingItem: null,
		};
	},
	computed: {
		selectedCountry() {
			return this.$route.params.country || '';
		},
		games() {
			return [...customItems.getItems('games'), ...GAMES];
		},
		filteredGames() {
			return this.selectedCountry !== '' ? this.games.filter((g) => g.country.includes(this.selectedCountry)) : this.games;
		},
		gamesPaginated() {
			let rarirtyFilteredGames =
				this.rarityFilter !== 'All' ? this.filteredGames.filter((g) => g.rarity === this.rarityFilter) : this.filteredGames;
			let ownFilteredGames = this.ownFilter !== undefined ? rarirtyFilteredGames.filter((g) => g.own === this.ownFilter) : rarirtyFilteredGames;
			return ownFilteredGames
				.filter(
					(g) =>
						g.title.toLocaleLowerCase().includes(this.modelValue.toLocaleLowerCase()) ||
						g.publisher.toLocaleLowerCase().includes(this.modelValue.toLocaleLowerCase()) ||
						g.country.toLocaleLowerCase().includes(this.modelValue.toLocaleLowerCase()) ||
						g.ean.toLocaleLowerCase().includes(this.modelValue.toLocaleLowerCase()) ||
						g.bigbox.toLocaleLowerCase().includes(this.modelValue.toLocaleLowerCase()) ||
						g.support.toLocaleLowerCase().includes(this.modelValue.toLocaleLowerCase()) ||
						g.rarity.toLocaleLowerCase().includes(this.modelValue.toLocaleLowerCase())
				)
				.slice(0, this.currentPage * this.perPage);
		},
	},
	methods: {
		filterGamesByCountry(c) {
			this.$router.push({ name: 'home', params: { country: c || undefined } });
		},
		openGameModal(g) {
			this.gameModalContent = g;
			this.showGameModal = true;
		},
		handleScroll() {
			if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 100) {
				this.currentPage++;
			} else if (window.scrollY === 0) {
				this.currentPage = 1;
			}
		},
		openAdd() {
			this.editingItem = null;
			this.showAddModal = true;
		},
		openEdit(item) {
			this.editingItem = item;
			this.showAddModal = true;
		},
		onModalClose() {
			this.showAddModal = false;
			this.editingItem = null;
		},
		async handleSubmit({ isEdit, id, payload }) {
			if (isEdit) {
				return customItems.updateItem('games', id, payload);
			}
			return customItems.addItem('games', payload);
		},
		async onDelete(item) {
			if (!confirm(`Delete "${item.title}"?`)) return;
			const result = await customItems.deleteItem('games', item.id);
			if (!result.ok) {
				alert(result.error || 'Could not delete this item.');
			}
		},
	},
	created() {
		this.currentPage = 1;
		window.addEventListener('scroll', this.handleScroll);
	},
	unmounted() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
ul {
	list-style: inside;
}
.admin-actions {
	display: flex;
	gap: 6px;
	flex-shrink: 0;
}
.icon-button {
	width: 28px;
	height: 28px;
	padding: 0;
	margin: 0;
	border: none;
	border-radius: 50%;
	background-color: rgba(255, 255, 255, 0.1);
	color: white;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	line-height: 1;
	font-size: 0.75rem;
	flex-shrink: 0;
}
.icon-button:hover {
	background-color: #48c78e;
	color: #131417;
}
.icon-button-delete:hover {
	background-color: #f14668;
	color: white;
}
</style>
